import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, useToast } from '@artosera/ui';
import { usePeut } from '../../auth/usePeut.js';
import { estDirectionDejaActive, renvoyerInvitation } from '../../lib/api.js';

/**
 * « Renvoyer l'invitation » de la direction d'une galerie qui n'a pas encore
 * accepté (lot B). Un nouveau lien part, l'ancien est révoqué ; le message dit
 * si le courriel est réellement parti. Au rôle qui provisionne
 * (`console:provision-gallery`), que l'API exige de son côté.
 */
export function RenvoiInvitation({
  idGalerie,
  compteId,
}: {
  idGalerie: string;
  /** Le compte de la ligne : l'API vérifie qu'il est direction en attente de cet espace. */
  compteId: string;
}): JSX.Element | null {
  const { t } = useTranslation('console');
  const peut = usePeut();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const mutation = useMutation({
    mutationFn: () => renvoyerInvitation(idGalerie, compteId),
    onSuccess: async (reponse) => {
      await queryClient.invalidateQueries({ queryKey: ['console', 'galerie', idGalerie] });
      toast({
        title: reponse.invitationEnvoyee
          ? t('invitationDirection.renvoyee', { email: reponse.email })
          : t('invitationDirection.nonRenvoyee', { email: reponse.email }),
        tone: reponse.invitationEnvoyee ? 'default' : 'danger',
        closeLabel: t('saisies.suppression.fermer'),
      });
    },
    onError: async (erreur) => {
      // Acceptée entre-temps : la fiche se relit, il n'y a plus rien à renvoyer.
      const dejaActive = estDirectionDejaActive(erreur);
      if (dejaActive) {
        await queryClient.invalidateQueries({ queryKey: ['console', 'galerie', idGalerie] });
      }
      toast({
        title: dejaActive ? t('invitationDirection.dejaActive') : t('invitationDirection.erreur'),
        tone: dejaActive ? 'default' : 'danger',
        closeLabel: t('saisies.suppression.fermer'),
      });
    },
  });

  if (!peut('console:provision-gallery')) return null;

  return (
    <Button
      variant="ghost"
      size="sm"
      loading={mutation.isPending}
      onClick={() => mutation.mutate()}
    >
      {mutation.isPending ? t('invitationDirection.renvoi') : t('invitationDirection.renvoyer')}
    </Button>
  );
}
